import { ECompareStatus, EModelContext } from "global/Compare/ComparisonEngine";
import { EMergeVMStatus, ESetVMType, IAttributeSetVM, IFlexibleFieldVM, INonStandardVM } from "global/Compare/ComparisonViewModelGenerator";
import { shouldIncludeComparisonItem } from "./Compare.constants";
import { CompareRow, buildLabel } from "./CompareRow";
import { CompareTitle } from "./CompareTitle";
import { IEntityComparePanelProps } from "./EntityComparePanel";
import { ECommentStatus, IComment, ICommentRouteRef, ICommentRouteType } from "model/FlexibleAttributeModel";
import { v4 as uuidv4 } from 'uuid';

interface IFlexibleCompareRowProps {
    // The value of the field in the origin.
    attribute: IFlexibleFieldVM;
    setFieldMergeAction?: (mergeAction: EMergeVMStatus) => void;
    entityProps: IEntityComparePanelProps;
}

const FlexibleCompareRow: React.FC<IFlexibleCompareRowProps> = (props) => {

    const includeItem = (changed?: boolean) => shouldIncludeComparisonItem(props.entityProps.configuration.showDifferencesOnly, changed);

    const bcbs_label = props.attribute.attributeDefinition.bcbs_config_attribute.bcbs_label!;

    let mergedValues = Object
        .entries(props.attribute.fieldVM?.merged?.mergedValues ?? {});
    // .sort(([keyA, valueA], [keyB, valueB]) => {
    //     const orderA = valueA?.onOrigin?.order ?? valueA?.onTarget?.order ?? 0;
    //     const orderB = valueB?.onOrigin?.order ?? valueB?.onTarget?.order ?? 0;
    //     return orderA - orderB;
    // });

    // if blanks (no values) are present, 
    // we want to ensure that they are displayed in the UI,
    // so we add a blank entry if there are no merged values
    if (mergedValues.length === 0)
        mergedValues.push(['primary', {
            onOrigin: undefined,
            onTarget: undefined,
            compareStatus: ECompareStatus.Identical,
            merged: undefined,
            primaryKey: props.attribute.attributeDefinition.bcbs_config_masterattribute.bcbs_config_masterattributeid!,
        }]);


    return (
        <>
            {mergedValues.map(([key, mergedValue]) => {

                const label = buildLabel(bcbs_label, mergedValue);

                const originValue = mergedValue?.onOrigin?.friendlyValue ?? '---';
                const targetValue = mergedValue?.onTarget?.friendlyValue ?? '---';

                const changed = mergedValue?.compareStatus !== ECompareStatus.Identical;

                const valueVM = {
                    origin: originValue,
                    target: targetValue,
                    changed: changed,
                    mergeAction: props.attribute.mergeAction,
                }

                const valueId = mergedValue?.onTarget?.value?.id ?? mergedValue?.onOrigin?.value?.id;

                const comment = valueId && props.entityProps.comments
                    ? props.entityProps.comments[valueId]
                    : undefined;

                const entityToCommentType = (context: EModelContext) => {
                    switch (context) {
                        case EModelContext.groupConfiguration:
                            return ICommentRouteType.root;
                        case EModelContext.plans:
                            return ICommentRouteType.plan;
                        case EModelContext.benefits:
                            return ICommentRouteType.benefit;
                        case EModelContext.accountsReceivable:
                            return ICommentRouteType.accountsReceivable;
                        case EModelContext.cesNumbers:
                            return ICommentRouteType.stopLoss;
                        case EModelContext.feesAndPremiums:
                            return ICommentRouteType.feesAndPremiums;
                        case EModelContext.groupNumbers:
                            return ICommentRouteType.groupNumber;
                        // case EModelContext.nonStandardTemplates:
                        //     return ICommentRouteType.nonStandardTemplate;
                        // case EModelContext.departments:
                        //     return ICommentRouteType.department;
                        // case EModelContext.cesNumbers:
                        //     return ICommentRouteType.cesNumber;    
                        default:
                            throw new Error(`Unsupported context for comment route type: ${context}`);
                    }
                }

                const createEntityLocation = (next?: ICommentRouteRef): ICommentRouteRef => {
                    return (
                        {
                            id: props.entityProps.compareVM.id,
                            name: props.entityProps.compareVM.name,
                            type: entityToCommentType(props.entityProps.compareVM.context),
                            next: next,
                        }
                    );
                }

                const createFieldValueLocation = (): ICommentRouteRef => {

                    const tab: ICommentRouteRef = {
                        id: props.attribute.attributeDefinition.bcbs_config_tab.bcbs_config_tabid!,
                        name: props.attribute.attributeDefinition.bcbs_config_tab.bcbs_name!,
                        type: ICommentRouteType.tab,
                        next: undefined,
                    }

                    const section: ICommentRouteRef = {
                        id: props.attribute.attributeDefinition.bcbs_config_section.bcbs_config_sectionid!,
                        name: props.attribute.attributeDefinition.bcbs_config_section.bcbs_name!,
                        type: ICommentRouteType.section,
                        next: undefined,
                    }

                    const config: ICommentRouteRef = {
                        id: props.attribute.attributeDefinition.bcbs_config_attribute.bcbs_config_attributeid!,
                        name: props.attribute.attributeDefinition.bcbs_config_attribute.bcbs_label!,
                        type: ICommentRouteType.configRef,
                        next: undefined,
                    }

                    const field: ICommentRouteRef = {
                        id: mergedValue?.onTarget?.field?.id ?? mergedValue?.onOrigin?.field?.id!,
                        name: mergedValue?.onTarget?.field?.name ?? mergedValue?.onOrigin?.field?.name ?? "",
                        type: ICommentRouteType.flexibleFieldRef,
                        next: undefined,
                    }


                    const value: ICommentRouteRef = {
                        id: valueId!,
                        name: label,
                        type: ICommentRouteType.flexibleFieldValueRef,
                        next: undefined,
                    }

                    const chain = chainLocations(
                        (next) => ({ ...tab, next }),
                        (next) => ({ ...section, next }),
                        (next) => ({ ...config, next }),
                        (next) => ({ ...field, next }),
                        () => value,
                    );

                    return chain;
                }

                // links a series of location builders (outermost first) into a single next-chain
                const chainLocations = (...builders: Array<(next?: ICommentRouteRef) => ICommentRouteRef>): ICommentRouteRef =>
                    builders.reduceRight<ICommentRouteRef | undefined>((next, build) => build(next), undefined)!;

                // the attribute's parent is either the entity itself, an attribute set, or a non-standard;
                // when it's the latter two, tab/section/set segments are chained in between to link them together
                const createNonStandardOrAttributeSetLocation = (next?: ICommentRouteRef): ICommentRouteRef => {
                    const parent = props.attribute.parent;

                    if (parent.type !== ESetVMType.attributeSet && parent.type !== ESetVMType.nonStandard) {
                        return next!;
                    }

                    const parentCasted = parent as IAttributeSetVM | INonStandardVM;

                    const createParentTab = (n?: ICommentRouteRef): ICommentRouteRef => {
                        const tab = parentCasted.tabDefinition.tab;
                        return {
                            id: tab.bcbs_config_tabid!,
                            name: tab.bcbs_name!,
                            type: ICommentRouteType.tab,
                            next: n,
                        };
                    }

                    const createParentSection = (n?: ICommentRouteRef): ICommentRouteRef => {
                        const section = parentCasted.sectionDefinition.section;
                        return {
                            id: section.bcbs_config_sectionid!,
                            name: section.bcbs_name!,
                            type: ICommentRouteType.section,
                            next: n,
                        };
                    }

                    const createParent = (n?: ICommentRouteRef): ICommentRouteRef => ({
                        id: parent.id,
                        name: parent.name,
                        type: parent.type === ESetVMType.attributeSet
                            ? ICommentRouteType.flexibleFieldSet
                            : ICommentRouteType.nonStandard,
                        next: n ?? next,
                    });

                    return chainLocations(
                        createParentTab,
                        createParentSection,
                        createParent,
                    );
                }

                const createNewComment = (): IComment => {
                    const comment: IComment = {
                        id: uuidv4(),
                        status: ECommentStatus.New,
                        comment: '',
                        created: {
                            on: new Date().toISOString(),
                            by: {
                                id: props.entityProps.authentication.principal?.systemUser?.systemuserid ?? 'unknown',
                                name: props.entityProps.authentication.principal?.systemUser?.fullname ?? 'unknown',
                            },
                        },
                        location: chainLocations(
                            createEntityLocation,
                            createNonStandardOrAttributeSetLocation,
                            createFieldValueLocation,
                        ),
                        onTarget: mergedValue?.onTarget?.value?.id === valueId,
                        onOrigin: mergedValue?.onOrigin?.value?.id === valueId,
                    }

                    debugger;
                    console.log('createNewComment:', comment);

                    return comment;
                }

                const onComment: (() => void) | undefined = props.entityProps.isUnderwritingApp //&& mergedValue?.onTarget
                    ? () => {
                        props.entityProps.onSetCommentInfo?.(
                            comment
                                ? structuredClone(comment)
                                : createNewComment(),
                            label,
                            mergedValue?.onOrigin?.value?.id === valueId,
                            mergedValue?.onTarget?.value?.id === valueId
                        )
                    }
                    : undefined;

                if (!includeItem(changed)) {
                    return null;
                }

                return (
                    <CompareRow
                        key={key}
                        data-config-attribute-id={props.attribute.attributeDefinition.bcbs_config_attribute.bcbs_config_attributeid}
                        data-config-master-attribute-id={props.attribute.attributeDefinition.bcbs_config_masterattribute.bcbs_config_masterattributeid}
                        data-value-origin-id={mergedValue?.onOrigin?.value?.id}
                        data-value-target-id={mergedValue?.onTarget?.value?.id}
                        name={<CompareTitle
                            changed={changed}
                            name={label}
                            entityProps={props.entityProps}
                        />}
                        valueVM={valueVM}
                        entityProps={props.entityProps}
                        onComment={onComment}
                        comment={comment}
                    />
                );
            })}
        </>
    );

};
export { FlexibleCompareRow };

